import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function MigraenePage() {
  const migraineTypes = [
    {
      title: "Klassisk migræne med aura",
      desc: "Synsforstyrrelser, flimren for øjnene, føleforstyrrelser eller svimmelhed forud for det dunkende hovedpineanfald.",
      icon: "fa-solid fa-eye",
    },
    {
      title: "Almindelig migræne uden aura",
      desc: "Intens, halvsidig bankende smerte, der forværres ved fysisk aktivitet og ledsages af stærk kvalme og lys- og lydoverfølsomhed.",
      icon: "fa-solid fa-head-side-virus",
    },
    {
      title: "Hormonel migræne & cyklus",
      desc: "Tilbagevendende migræneanfald koblet til menstruationscyklus, hormonelle udsving eller overgangsalder.",
      icon: "fa-solid fa-dna",
    },
    {
      title: "Kronisk spændingshovedpine",
      desc: "Konstant trykkende bånd om panden og tindingerne udløst af stress, synsbelastning eller spændinger i nakke og kæbeled.",
      icon: "fa-solid fa-brain",
    },
  ];

  const whyJapaneseWorks = [
    {
      title: "Ultra-tynde 0,12 mm nåle",
      desc: "Mennesker med migræne har ofte et overstimuleret nervesystem. Japanske nåle er så tynde, at behandlingen er stort set smertefri og ikke overbelaster sanserne.",
    },
    {
      title: "Regulering af blodkarrene",
      desc: "Migræne opstår ved kramper og udvidelser af blodkar i hjernen. Nænsom akupunktur afbalancerer det autonome nervesystem og dæmper denne krampetilbøjelighed.",
    },
    {
      title: "Helhedsorienteret puls- og mavediagnose",
      desc: "Inden nålene sættes, mærker jeg på din puls og mave (Hara) for at afdække, hvor i kroppens energibaner ubalancen stammer fra.",
    },
    {
      title: "Mulighed for ikke-penetrerende kontaktnåle",
      desc: "Er du under et igangværende anfald eller meget sensitiv, kan vi anvende japanske kontaktnåle (Teishin), som stimulerer huden helt uden indstik.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktur mod Migræne | Risskov & Aarhus</title>
        <meta
          name="description"
          content="Mindre migræne og færre spændingshovedpiner med japansk akupunktur i Risskov. Få afklaring og en individuel behandlingsplan hos Kita Akupunktur."
        />
        <meta
          property="og:title"
          content="Akupunktur mod Migræne | Risskov & Aarhus"
        />
        <meta
          property="og:description"
          content="Mindre migræne og færre spændingshovedpiner med japansk akupunktur i Risskov. Få afklaring og en individuel behandlingsplan hos Kita Akupunktur."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/migraene" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Japansk akupunktur mod migræne i Risskov",
            "url": "https://kita-akupunktur.dk/migraene",
            "description":
              "Nænsom japansk akupunkturbehandling ved migræne og spændingshovedpine i Risskov nær Aarhus.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/japansk-akupunktur" className="hover:text-[#d74814] transition-colors">
                  Japansk akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Migræne
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>JAPANSK AKUPUNKTUR • KEIRAKU CHIRYO</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Nænsom akupunktur mod migræne og kraftig hovedpine i Risskov
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Når migrænen rammer, lukker verden ned. Med traditionel japansk meridianterapi arbejder jeg med ultra-tynde nåle og stor nænsomhed for at berolige dit nervesystem, reducere anfaldsfrekvensen og give dig hverdagen tilbage uden konstant frygt for næste anfald.
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Bestil tid til migrænehjælp</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering ved klinikken</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-20 bg-[#fffaf7] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* SEO Intro Section: Akupunktur mod migræne i Risskov nær Aarhus */}
          <div className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-[0_8px_30px_rgba(89,52,39,0.04)] relative overflow-hidden">
            <div className="max-w-4xl space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>INDIVIDUELT TILPASSET BEHANDLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427] leading-snug">
                Akupunktur mod migræne i Risskov nær Aarhus
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Overvejer du akupunktur mod hovedpine i Aarhus? Hos{" "}
                  <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
                    Kita Akupunktur
                  </Link>{" "}
                  i Risskov tilbyder jeg individuelt tilpassede forløb ved migræne og spændingshovedpine, også til dig fra Jystrup. Vi tager udgangspunkt i dine gener og din hverdag, og jeg tilpasser behandlingen efter din sensitivitet. Du får god plads til at stille spørgsmål undervejs.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Er det migræne eller spændingshovedpine? */}
          <div className="space-y-6">
            <div className="max-w-3xl space-y-2">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FORSKELLE & SYMPTOMER</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Er det migræne eller spændingshovedpine?
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                At forstå forskellen på de to hovedpinetyper er afgørende for at tilrettelægge den rette behandling og aflaste kroppen bedst muligt.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {/* Migræne Card */}
              <div className="bg-white/95 p-7 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-sm flex flex-col justify-between space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className="fa-solid fa-head-side-virus text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-serif font-semibold text-xl text-[#593427]">
                        Migræne
                      </h3>
                      <span className="text-xs text-[#d74814] font-medium">Halvsidig & dunkende</span>
                    </div>
                  </div>
                  <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                    Migræne giver ofte en dunkende hovedpine, som kan sidde i den ene side af hovedet. Kvalme og følsomhed over for lys og lyd kan følge med, og almindelig aktivitet kan forværre smerten. Nogle oplever også aura med for eksempel synsforstyrrelser.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#ffe0cf]/80 text-xs text-[#7a5b4f] flex items-center space-x-2 bg-[#fffaf7] p-3 rounded-xl">
                  <i className="fa-solid fa-circle-info text-[#d74814]" aria-hidden="true" />
                  <span>Er du i tvivl om din hovedpine, bør din læge vurdere den.</span>
                </div>
              </div>

              {/* Spændingshovedpine Card */}
              <div className="bg-white/95 p-7 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-sm flex flex-col justify-between space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className="fa-solid fa-brain text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-serif font-semibold text-xl text-[#593427]">
                        Spændingshovedpine
                      </h3>
                      <span className="text-xs text-[#d74814] font-medium">Trykkende bånd & muskler</span>
                    </div>
                  </div>
                  <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                    Spændingshovedpine føles oftere som et trykkende bånd omkring hovedet og rammer typisk begge sider. Den kan optræde sammen med ømme muskler i nakke, skuldre eller kæbe.
                  </p>
                </div>
                <div className="pt-3 border-t border-[#ffe0cf]/80 text-xs text-[#593427]">
                  <p className="mb-2">
                    Har du også gener fra nakken, kan du læse om mit tilbud ved spændinger i nakke og skulder:
                  </p>
                  <Link
                    to="/spaendinger"
                    className="inline-flex items-center space-x-1.5 font-semibold text-[#d74814] hover:underline"
                  >
                    <span>Læs om spændinger i nakke og skulder</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Når du har kronisk hovedpine & Akut lægehjælp */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>KRONISKE SMERTER</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Når du har kronisk hovedpine
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
                <p>
                  Kronisk hovedpine betegner typisk hovedpine mindst 15 dage om måneden gennem mere end tre måneder. Det kræver en lægelig vurdering, så du får afklaret typen og de relevante muligheder for behandling.
                </p>
                <p>
                  Hyppig brug af smertestillende medicin kan også bidrage til hovedpine. Tal med lægen, før du ændrer din medicin.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-[#fff5ee] p-7 sm:p-8 rounded-3xl border-2 border-[#d74814]/30 shadow-sm space-y-4">
              <div className="flex items-center space-x-2.5 text-[#d74814]">
                <i className="fa-solid fa-triangle-exclamation text-xl" aria-hidden="true" />
                <h3 className="font-serif font-bold text-lg text-[#593427]">
                  Vigtig lægelig vurdering
                </h3>
              </div>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Ny eller ændret hovedpine bør vurderes af lægen. Ring 112 ved pludselig, meget voldsom hovedpine eller hovedpine med nye lammelser, talebesvær eller påvirket bevidsthed.
              </p>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Hovedpine med feber og nakkestivhed kræver også akut lægehjælp. Akupunktur må ikke forsinke en nødvendig undersøgelse.
              </p>
            </div>
          </div>

          {/* Section: Akupunktur som supplement til din behandling af migræne */}
          <div className="glass-sakura p-8 sm:p-12 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>SUPPLERENDE BEHANDLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Akupunktur som supplement til din behandling af migræne
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-white/90 p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] space-y-3">
                <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2">
                  <i className="fa-solid fa-stethoscope text-[#d74814]" aria-hidden="true" />
                  <span>Forebyggelse og realistiske rammer</span>
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Akupunktur er undersøgt som forebyggende behandling ved migræne, og studier peger på færre hovedpinedage hos nogle. Effekten varierer, og resultater for akupunktur generelt kan ikke uden videre overføres til alle japanske teknikker. Behandlingen erstatter ikke lægens udredning eller den medicin, du har fået ordineret.
                </p>
              </div>

              <div className="bg-white/90 p-6 sm:p-7 rounded-2xl border border-[#ffe0cf] space-y-3">
                <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2">
                  <i className="fa-solid fa-book-medical text-[#d74814]" aria-hidden="true" />
                  <span>Hverdag, søvn og hovedpinedagbog</span>
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Ved behandling af spændingshovedpine taler vi også om søvn, pauser og belastninger i din hverdag. En hovedpinedagbog kan hjælpe dig med at notere smerter, medicin og mulige mønstre. Den giver os et mere konkret grundlag for at følge dit forløb og vurdere, om du oplever en ændring, som har betydning for dig.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Sådan foregår dit forløb hos mig */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FORLØBET HOS KITA AKUPUNKTUR</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Sådan foregår dit forløb hos mig
              </h2>
              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed">
                Dit forløb begynder med en samtale om dine gener og dine ønsker. Jeg hedder Mayumi Kita Thiele og er registreret alternativ behandler, RAB.{" "}
                <Link
                  to="/om-mig"
                  className="text-[#d74814] font-medium underline hover:text-[#b83b0e] transition-colors"
                >
                  Du kan læse mere om min baggrund og uddannelse
                </Link>
                . Inden behandlingen taler vi blandt andet om følgende, så jeg kan tilpasse forløbet til dig:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#ffe0cf]/60 text-[#d74814] flex items-center justify-center font-serif font-bold text-lg">
                  1
                </div>
                <h3 className="font-serif font-semibold text-base text-[#593427]">
                  Frekvens & varighed
                </h3>
                <p className="text-sm text-[#7a5b4f] leading-relaxed">
                  Hvor ofte du har hovedpine, og hvor længe den varer.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#ffe0cf]/60 text-[#d74814] flex items-center justify-center font-serif font-bold text-lg">
                  2
                </div>
                <h3 className="font-serif font-semibold text-base text-[#593427]">
                  Lægevurdering & medicin
                </h3>
                <p className="text-sm text-[#7a5b4f] leading-relaxed">
                  Hvad lægen har vurderet, og hvilken medicin du bruger.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#ffe0cf]/60 text-[#d74814] flex items-center justify-center font-serif font-bold text-lg">
                  3
                </div>
                <h3 className="font-serif font-semibold text-base text-[#593427]">
                  Erfaringer & tryghed
                </h3>
                <p className="text-sm text-[#7a5b4f] leading-relaxed">
                  Dine tidligere erfaringer med behandling og eventuel utryghed ved nåle.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Japansk akupunktur tilpasset din sensitivitet */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>NÆNSOMMET & TRYGHED</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Japansk akupunktur tilpasset din sensitivitet
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
                <p>
                  Min primære tilgang er japansk akupunktur, hvor jeg arbejder med meget tynde, sterile engangsnåle. Hvis du er utryg ved indstik, kan vi tale om kontaktnåle, som berører huden uden at trænge ind. Du kan stadig mærke behandlingen, og vi tilpasser den løbende efter dine reaktioner og det, du føler dig tryg ved.
                </p>
                <p>
                  Ved japansk akupunktur kan en traditionel vurdering af puls og mave indgå i valget af punkter. Det er en del af akupunkturens metode og ikke en lægelig undersøgelse. Ved næste besøg følger vi op på din oplevelse og eventuelle ændringer i hovedpinen. Der er ikke ét fast antal behandlinger, som passer til alle.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white/95 p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-[0_8px_30px_rgba(89,52,39,0.06)] space-y-4">
              <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2 border-b border-[#ffe0cf]/80 pb-3">
                <i className="fa-solid fa-sparkles text-[#d74814]" aria-hidden="true" />
                <span>Hvad kan du opnå med et forløb?</span>
              </h3>
              <ul className="space-y-3 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Mærkbar reduktion i antallet af ugentlige/månedlige anfald</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Kortere varighed og mildere intensitet, når et anfald opstår</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Dæmpning af kvalme, svimmelhed og lys-/lydfølsomhed</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Mindre behov for triptaner og smertestillende medicin</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Roligere søvn og mere overskud i hverdagen</span>
                </li>
              </ul>

              <div className="pt-2">
                <div className="p-3.5 rounded-xl bg-[#ffe0cf]/30 border border-[#ffe0cf] text-xs text-[#593427] flex items-center space-x-3">
                  <JapaneseBadge className="shrink-0" />
                  <span>RAB-godkendt: Mulighed for tilskud fra Sygeforsikringen "danmark" og de fleste sundhedsforsikringer.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Types of Migraines */}
          <div className="space-y-6">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Former for hovedpine og migræne jeg behandler
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                Uanset om dine anfald er hormonelle, stressrelaterede eller ledsaget af aura, tilpasser jeg behandlingen præcist til dig.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {migraineTypes.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white/90 p-6 rounded-2xl border border-[#ffe0cf] hover:border-[#d74814]/40 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="w-11 h-11 rounded-xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className={`${item.icon} text-lg`} aria-hidden="true" />
                    </div>
                    <h3 className="font-serif font-semibold text-base text-[#593427]">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Treatment Pillars */}
          <div className="glass-sakura p-8 sm:p-12 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>TRYG BEHANDLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Den japanske tilgang til migrænebehandling
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f] mt-2">
                Hver detalje i klinikken er indrettet med henblik på ro, dæmpet belysning og fuldstændig tryghed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whyJapaneseWorks.map((pillar, i) => (
                <div
                  key={i}
                  className="bg-white/85 p-6 rounded-2xl border border-[#ffe0cf] space-y-2.5"
                >
                  <h3 className="font-serif font-semibold text-base text-[#593427] flex items-center space-x-2">
                    <SakuraFlower className="w-4 h-4 text-[#d74814]" />
                    <span>{pillar.title}</span>
                  </h3>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                    {pillar.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Related pages links */}
          <div className="p-6 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-center sm:text-left">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                Relaterede behandlingsområder
              </div>
              <p className="text-sm text-[#593427] font-serif">
                Hovedpine og migræne stammer ofte fra spændinger i nakken eller kæben
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Link
                to="/ondt-i-kaeben"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Ondt i kæben →
              </Link>
              <Link
                to="/spaendinger"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Spændinger i nakken →
              </Link>
              <Link
                to="/stress"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Stress & uro →
              </Link>
              <Link
                to="/japansk-akupunktur"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Japansk akupunktur →
              </Link>
              <Link
                to="/led-og-muskelsmerter"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Led- & muskelsmerter →
              </Link>
            </div>
          </div>

          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskud fra “danmark”
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi Kita Thiele
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>

          {/* Contact and Booking Section: Vil du høre mere om akupunktur mod migræne i Aarhus? */}
          <div className="pt-6">
            <div className="text-center max-w-3xl mx-auto mb-10 space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>KONTAKT & TIDSBESTILLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427]">
                Vil du høre mere om akupunktur mod migræne i Aarhus?
              </h2>
              <div className="text-base text-[#7a5b4f] leading-relaxed space-y-3">
                <p>
                  Du finder Kita Akupunktur på Kantorparken 11, 8240 Risskov, hvor der er gratis parkering foran huset. Her foregår behandlingen efter aftale i rolige rammer. Du er velkommen til at spørge ind til metoden, før du bestiller tid, især hvis du er følsom over for nåle eller er i tvivl om, hvad et forløb indebærer.
                </p>
                <p>
                  Ring til mig på{" "}
                  <a
                    href="tel:+4529387186"
                    className="font-semibold text-[#d74814] hover:underline"
                  >
                    29 38 71 86
                  </a>
                  , eller kontakt mig om en tid via formularen nedenfor. Fortæl gerne kort om din hovedpine, og om du allerede er blevet undersøgt hos lægen. Så kan vi tale om, hvad jeg tilbyder, og om et individuelt forløb hos mig er relevant som supplement til din øvrige behandling.
                </p>
              </div>
            </div>
            <ContactForm />
          </div>

          {/* FAQ Section sidst på siden */}
          <div className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
            <FaqSection
              title="Ofte stillede spørgsmål om migræne og hovedpine"
              subtitle="Få svar på hvordan skånsom akupunktur forebygger migræneanfald, dæmper spændingshovedpine og beroliger overbelastede nervebaner."
              faqs={[
                {
                  question: "Hvordan kan akupunktur forebygge migræneanfald og spændingshovedpine?",
                  answer: (
                    <>
                      Akupunktur virker forebyggende ved at regulere tonus i hjernens blodkar og dæmpe overfølsomheden i trigeminusnerven, som er den primære smertesti ved migræneanfald. Samtidig afspændes de dybe{" "}
                      <Link to="/spaendinger" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        nakkemuskler
                      </Link>{" "}
                      og{" "}
                      <Link to="/ondt-i-kaeben" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        kæbespændinger
                      </Link>
                      , hvor infiltrationer ofte trigger spændingshovedpine. Ved at balancere nervesystemet hæves tærsklen for, hvad der udløser et anfald i hverdagen.
                    </>
                  ),
                },
                {
                  question: "Hvor mange behandlinger skal der til, før jeg mærker en reduktion i anfald?",
                  answer:
                    "Et typisk forebyggende forløb mod migræne strækker sig over 5 til 8 ugentlige sessioner. Mange oplever allerede efter de første 3-4 behandlinger, at anfaldene bliver mindre intense, kortere i varighed, eller at der går mærkbart længere tid imellem dem. Når kroppen er stabiliseret, overgår mange til en vedligeholdende behandling en gang om måneden for at fastholde roen.",
                },
                {
                  question: "Må jeg modtage akupunktur, mens jeg har et igangværende migræneanfald?",
                  answer:
                    "Ja, men behandlingen tilpasses helt anderledes under et akut anfald. Her undgår vi kraftige stimuli i hovedområdet og benytter i stedet ekstremt blide, distale punkter på underarme, fødder og ører for at trække spænding og blodtryk væk fra kraniet. Dette hjælper med at dæmpe kvalme, lette det dunkende tryk for tindingerne og fremme en tiltrængt hvilepause.",
                },
                {
                  question: "Kan jeg fortsætte med min migrænemedicin (f.eks. triptaner) sideløbende?",
                  answer: (
                    <>
                      Ja, akupunktur kan fuldstændig trygt kombineres med enhver form for lægeordineret forebyggende eller anfaldskuperende migrænemedicin. Da jeg er RAB-godkendt, yder Sygeforsikring “danmark” desuden tilskud ved en lægelig diagnose. Se mere under{" "}
                      <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        priser og tilskud
                      </Link>
                      .
                    </>
                  ),
                },
                {
                  question: "Sættes nålene direkte i ansigtet og hovedet, og gør det ondt?",
                  answer: (
                    <>
                      Der kan anvendes enkelte hårfine nåle i pande eller tindinger, men størstedelen af de mest virksomme punkter sidder på underarme, underben og i ørerne. Jeg anvender{" "}
                      <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        japansk akupunktur
                      </Link>{" "}
                      med ultratynde nåle (helt ned til 0,12 mm), hvilket gør indstikket nærmest umærkeligt. Behandlingen opleves som behagelig og dybt afspændende.
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
